import { useCallback, useEffect, useMemo, useState } from "react";
import { getVolunteerTournamentParticipantsList } from "../api/tournamentRegistrationApi";
import { GlobalModal } from "../../../global/GlobalModal";
import GlobalFeedback from "../../../global/GlobalFeedback";
import ParticipantsListMetricsSection from "../components/participants/sections/ParticipantsListMetricsSection";
import ParticipantsListLabelFiltersSection from "../components/participants/sections/ParticipantsListLabelFiltersSection";
import ParticipantsListDirectorySection from "../components/participants/sections/ParticipantsListDirectorySection";
import {
  COLUMN_DEFS,
  PAGE_SIZE,
} from "../components/participants/participantsListConstants";
import {
  compareByColumn,
  createInitialColumnVisibility,
  formatDateCell,
  formatMoney,
  remarksDisplay,
  yesNo,
} from "../components/participants/participantsListHelpers";
import "../styles/participantsList.css";
import { getJwtRoleSlug } from "../../../public/utils/jwtRole";

export default function ParticipantsListPage({ adminTournamentFeatures: adminTournamentFeaturesProp } = {}) {
  const roleSlug = getJwtRoleSlug(
    typeof localStorage !== "undefined" ? localStorage.getItem("jwt_token") : null,
  );
  const adminTournamentFeatures =
    adminTournamentFeaturesProp !== undefined
      ? adminTournamentFeaturesProp
      : roleSlug !== "volunteer";

  const isAdminView =
    typeof window !== "undefined" &&
    (window.location.pathname.startsWith("/app/tournaments/participants") ||
      window.location.pathname.startsWith("/admin") ||
      window.location.pathname.startsWith("/sub-admin"));

  const [rows, setRows] = useState([]);
  const [tournamentOptions, setTournamentOptions] = useState([]);
  const [stats, setStats] = useState({
    registered_today_count: 0,
    approved_count: 0,
    total_participants: 0,
    completed_tournaments_count: 0,
  });
  const [tournamentTimingFilter, setTournamentTimingFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const initialListFilters = {
    student_search: "",
    column: "",
    value: "",
  };
  const [draftFilters, setDraftFilters] = useState(() => ({
    ...initialListFilters,
  }));
  const [appliedFilters, setAppliedFilters] = useState(() => ({
    ...initialListFilters,
  }));

  const [sortColumn, setSortColumn] = useState("payment_date");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [columnVisibility, setColumnVisibility] = useState(
    createInitialColumnVisibility,
  );

  const [detailRow, setDetailRow] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setAppliedFilters({ ...draftFilters }), 400);
    return () => clearTimeout(t);
  }, [draftFilters]);

  const loadList = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const queryFilters = {
        student_search: appliedFilters.student_search || undefined,
        sort_by: sortColumn,
        order: sortOrder,
        page,
        limit: PAGE_SIZE,
      };
      if (
        adminTournamentFeatures &&
        tournamentTimingFilter &&
        tournamentTimingFilter !== "all"
      ) {
        queryFilters.tournament_timing = tournamentTimingFilter;
      }
      if (
        appliedFilters.column &&
        appliedFilters.value !== undefined &&
        String(appliedFilters.value).trim() !== ""
      ) {
        queryFilters.filter_column = appliedFilters.column;
        queryFilters.filter_value = appliedFilters.value;
      }
      const res = await getVolunteerTournamentParticipantsList({
        ...queryFilters,
      });
      setRows(Array.isArray(res.data) ? res.data : []);
      setTournamentOptions(res.meta?.tournaments || []);
      setTotal(Number(res.meta?.total || 0));
      setTotalPages(Math.max(1, Number(res.meta?.totalPages || 1)));
      setPage(Number(res.meta?.page || 1));
      setStats(
        res.meta?.stats || {
          registered_today_count: 0,
          approved_count: 0,
          total_participants: 0,
          completed_tournaments_count: 0,
        },
      );
    } catch (e) {
      setErrorMsg(e?.message || "Could not load participants.");
      setRows([]);
      setTournamentOptions([]);
      setTotal(0);
      setTotalPages(1);
      setStats({
        registered_today_count: 0,
        approved_count: 0,
        total_participants: 0,
        completed_tournaments_count: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [
    appliedFilters,
    sortColumn,
    sortOrder,
    page,
    adminTournamentFeatures,
    tournamentTimingFilter,
  ]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    setPage(1);
  }, [appliedFilters, sortColumn, sortOrder, tournamentTimingFilter]);

  const visibleDataColumns = useMemo(
    () => COLUMN_DEFS.filter((c) => columnVisibility[c.id]),
    [columnVisibility],
  );

  const sortedRows = useMemo(() => {
    const col = COLUMN_DEFS.find((c) => c.id === sortColumn && c.sortable);
    if (!col) return rows;
    const mult = sortOrder === "ASC" ? 1 : -1;
    const list = [...rows];
    list.sort((a, b) => mult * compareByColumn(a, b, sortColumn));
    return list;
  }, [rows, sortColumn, sortOrder]);

  const handleSortClick = useCallback((col) => {
    if (!col.sortable) return;
    if (sortColumn === col.id) {
      setSortOrder((o) => (o === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortColumn(col.id);
      setSortOrder("ASC");
    }
  }, [sortColumn]);

  const valueFilterOptions = useMemo(() => {
    if (!draftFilters.column) return [];
    if (
      draftFilters.column === "tournament_name" &&
      adminTournamentFeatures &&
      tournamentOptions.length > 0
    ) {
      return tournamentOptions
        .map((t) => {
          const name = String(t.tournament_name ?? "").trim();
          if (!name) return null;
          const datePart = t.tournament_date
            ? formatDateCell(t.tournament_date)
            : "";
          return {
            value: name,
            label: datePart ? `${name} (${datePart})` : name,
          };
        })
        .filter(Boolean)
        .sort((a, b) =>
          a.label.localeCompare(b.label, undefined, {
            numeric: true,
            sensitivity: "base",
          }),
        );
    }
    if (
      draftFilters.column === "payment_verified" ||
      draftFilters.column === "registration_verified"
    ) {
      return [
        { value: "Verified", label: "Verified" },
        { value: "Not verified", label: "Not verified" },
      ];
    }
    if (draftFilters.column === "payment_proof") {
      return [
        { value: "1", label: "Yes" },
        { value: "0", label: "No" },
      ];
    }
    const seen = new Set();
    const options = [];
    for (const row of rows) {
      const raw = row[draftFilters.column];
      if (raw == null || raw === "") continue;
      const label = String(raw);
      if (!seen.has(label)) {
        seen.add(label);
        options.push({ value: label, label });
      }
    }
    return options.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, {
        numeric: true,
        sensitivity: "base",
      }),
    );
  }, [draftFilters.column, rows, adminTournamentFeatures, tournamentOptions]);

  const metricItems = useMemo(() => {
    const base = [
      {
        key: "registered_today_count",
        label: "Members registered today",
        value: Number(stats.registered_today_count || 0),
        tone: "mint",
      },
      {
        key: "approved_count",
        label: "Applications approved",
        value: Number(stats.approved_count || 0),
        tone: "teal",
      },
      {
        key: "total_participants",
        label: "Total participants",
        value: Number(stats.total_participants || 0),
        tone: "slate",
      },
    ];
    if (!adminTournamentFeatures) return base;
    return [
      ...base,
      {
        key: "completed_tournaments_count",
        label: "Tournaments completed",
        value: Number(stats.completed_tournaments_count || 0),
        tone: "coral",
      },
    ];
  }, [stats, adminTournamentFeatures]);

  const toggleTournamentTimingFilter = useCallback((next) => {
    setTournamentTimingFilter((curr) => (curr === next ? "all" : next));
  }, []);

  return (
    <div className="vpart-page">
      <div className="vpart-page__stack">
        <header className="vpart-hero">
          <p className="vpart-hero__eyebrow">
            {isAdminView ? "Administration" : "Tournaments"}
          </p>
          <h1 className="vpart-hero__title">
            {isAdminView
              ? "Tournament Participants"
              : "Participants you registered"}
          </h1>
          <p className="vpart-hero__sub">
            {isAdminView
              ? "All participants registered through volunteer tournament registrations, with tournament and payment information."
              : "Students you added through tournament registration, with tournament and payment information."}{" "}
            Use filters and column visibility to focus the table; click a column
            header to sort.
          </p>
        </header>

        <GlobalFeedback type="error" text={errorMsg} />

        <ParticipantsListMetricsSection items={metricItems} loading={loading} />

        {adminTournamentFeatures ? (
          <ParticipantsListLabelFiltersSection
            tournamentTimingFilter={tournamentTimingFilter}
            onSetTimingAll={() => setTournamentTimingFilter("all")}
            onToggleTiming={toggleTournamentTimingFilter}
          />
        ) : null}

        <ParticipantsListDirectorySection
          draftFilters={draftFilters}
          setDraftFilters={setDraftFilters}
          valueFilterOptions={valueFilterOptions}
          adminTournamentFeatures={adminTournamentFeatures}
          columnVisibility={columnVisibility}
          onVisibilityChange={setColumnVisibility}
          visibleDataColumns={visibleDataColumns}
          sortedRows={sortedRows}
          loading={loading}
          sortColumn={sortColumn}
          sortOrder={sortOrder}
          onSortClick={handleSortClick}
          page={page}
          totalPages={totalPages}
          total={total}
          onPageChange={setPage}
          onViewDetails={setDetailRow}
        />
      </div>

      <GlobalModal
        open={detailRow != null}
        onClose={() => setDetailRow(null)}
        title="Registration details"
        description={
          detailRow
            ? `${detailRow.student_name || "Student"} — ${detailRow.tournament_name || "Tournament"}`
            : undefined
        }
        size="lg"
        classNames={{
          modal: "gff-modal vpart-reg-modal",
          header: "gff-modal__header vpart-reg-modal__header",
          title: "gff-modal__title vpart-reg-modal__title",
          description: "gff-modal__description vpart-reg-modal__subtitle",
          body: "gff-modal__body vpart-reg-modal__body",
        }}
      >
        {detailRow ? (
          <div className="vpart-reg-modal__scroll">
            <article className="vpart-reg-card">
              <h3 className="vpart-reg-card__title">Student</h3>
              <div className="vpart-reg-fields">
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Code</span>
                  <span className="vpart-reg-field__value">
                    {detailRow.student_code ?? "—"}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Name</span>
                  <span className="vpart-reg-field__value">
                    {detailRow.student_name ?? "—"}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Contact</span>
                  <span className="vpart-reg-field__value">
                    {detailRow.student_contact ?? "—"}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Email</span>
                  <span className="vpart-reg-field__value">
                    {detailRow.student_email ?? "—"}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Gender</span>
                  <span className="vpart-reg-field__value">
                    {detailRow.student_gender ?? "—"}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Date of birth</span>
                  <span className="vpart-reg-field__value">
                    {formatDateCell(detailRow.student_date_of_birth)}
                  </span>
                </div>
              </div>
            </article>
            <article className="vpart-reg-card">
              <h3 className="vpart-reg-card__title">Tournament</h3>
              <div className="vpart-reg-fields">
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Name</span>
                  <span className="vpart-reg-field__value">
                    {detailRow.tournament_name ?? "—"}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Code</span>
                  <span className="vpart-reg-field__value">
                    {detailRow.tournament_code ?? "—"}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Date</span>
                  <span className="vpart-reg-field__value">
                    {formatDateCell(detailRow.tournament_date)}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Location</span>
                  <span className="vpart-reg-field__value">
                    {detailRow.tournament_location ?? "—"}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Sport</span>
                  <span className="vpart-reg-field__value">
                    {detailRow.sport_name ?? "—"}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Level</span>
                  <span className="vpart-reg-field__value">
                    {detailRow.tournament_level_name ?? "—"}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Participation fee</span>
                  <span className="vpart-reg-field__value">
                    {formatMoney(detailRow.participation_fee)}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Registered on</span>
                  <span className="vpart-reg-field__value">
                    {formatDateCell(detailRow.tour_registration_date)}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">
                    Tournament registration verified
                  </span>
                  <span className="vpart-reg-field__value">
                    {yesNo(detailRow.registration_verified)}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Certificate template</span>
                  <span className="vpart-reg-field__value">
                    {detailRow.certificate_template_name ?? "—"}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Certificate status</span>
                  <span className="vpart-reg-field__value">
                    {detailRow.certificate_status_name ?? "—"}
                  </span>
                </div>
              </div>
            </article>
            <article className="vpart-reg-card vpart-reg-card--payment">
              <h3 className="vpart-reg-card__title">Payment</h3>
              <div className="vpart-reg-fields">
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Receipt</span>
                  <span className="vpart-reg-field__value">
                    {detailRow.receipt_no ?? "—"}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Amount</span>
                  <span className="vpart-reg-field__value">
                    {formatMoney(detailRow.amount_paid)}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Date / time</span>
                  <span className="vpart-reg-field__value">
                    {formatDateCell(detailRow.payment_date)}
                    {detailRow.payment_time ? ` · ${detailRow.payment_time}` : ""}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Mode</span>
                  <span className="vpart-reg-field__value">
                    {detailRow.payment_mode_name ?? "—"}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Sub-type</span>
                  <span className="vpart-reg-field__value">
                    {detailRow.payment_sub_type_name ?? "—"}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Purpose</span>
                  <span className="vpart-reg-field__value">
                    {detailRow.purpose_name ?? "—"}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Transaction ID</span>
                  <span className="vpart-reg-field__value">
                    {detailRow.transaction_id ?? "—"}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Payment verified</span>
                  <span className="vpart-reg-field__value">
                    {yesNo(detailRow.payment_verified)}
                  </span>
                </div>
                {remarksDisplay(detailRow.payment_remarks) ? (
                  <div className="vpart-reg-field vpart-reg-field--full">
                    <span className="vpart-reg-field__label">Remarks</span>
                    <span className="vpart-reg-field__value vpart-reg-field__value--remarks">
                      {remarksDisplay(detailRow.payment_remarks)}
                    </span>
                  </div>
                ) : null}
              </div>
            </article>
          </div>
        ) : null}
      </GlobalModal>
    </div>
  );
}

